import { PublicClientApplication, InteractionRequiredAuthError, BrowserAuthError } from '@azure/msal-browser';
import getConfig from '../config';
import getApiConfig from '../config/apiConfig';

const config = getConfig();

const msalConfig = {
  auth: {
    clientId: config.clientId,
    authority: `${config.authority}${config.tenantId}`,
    redirectUri: config.redirectUri,
    postLogoutRedirectUri: config.postLogoutRedirectUri,
    navigateToLoginRequestUrl: false
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: true,
  }
};

// Helper function to handle API responses
const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error);
  }
  return response.json();
};

// Create the MSAL instance
export const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL
msalInstance.initialize().then(() => {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
  }
});

// Login request configuration
export const loginRequest = {
  scopes: config.scopes
};

// Get token function that works with both MSAL and local API
export const getToken = async () => {
  let account = msalInstance.getActiveAccount();
  if (!account) {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      account = accounts[0];
      msalInstance.setActiveAccount(account);
    } else {
      throw new Error('No active account! Verify a user has been signed in and setActiveAccount has been called.');
    }
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: account
    });
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError) {
      try {
        const response = await msalInstance.acquireTokenPopup(loginRequest);
        return response.accessToken;
      } catch (err) {
        throw err;
      }
    } else {
      throw error;
    }
  }
};

// Logout function that works with both MSAL and local API
export const logout = async () => {
  const account = msalInstance.getActiveAccount();
  if (account) {
    try {
      // First, call the local API logout
      const { apiBaseUrl } = getApiConfig();
      await fetch(`${apiBaseUrl}/auth/logout`, {
        method: 'POST'
      });
      
      // Then, logout from MSAL
      await msalInstance.logoutRedirect({
        account: account,
        postLogoutRedirectUri: config.postLogoutRedirectUri
      });
    } catch (error) {
      console.error('Error during logout:', error);
      // Still try to logout from MSAL even if API logout fails
      await msalInstance.logoutRedirect({
        account: account,
        postLogoutRedirectUri: config.postLogoutRedirectUri
      });
    }
  }
};

// The main auth service object with local API endpoints
export const authService = {
  // Get token using local API
  async getTokenFromApi() {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/auth/token`);
      return handleResponse(response);
    } catch (error) {
      console.error('Error getting token:', error);
      throw error;
    }
  },

  // Login using local API
  async login(credentials) {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(credentials)
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error logging in:', error);
      throw error;
    }
  },

  // Check if user is authenticated
  async isAuthenticated() {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/auth/check`);
      return handleResponse(response);
    } catch (error) {
      console.error('Error checking authentication:', error);
      throw error;
    }
  },

  // Get current user
  async getCurrentUser() {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/auth/me`);
      return handleResponse(response);
    } catch (error) {
      console.error('Error getting current user:', error);
      throw error;
    }
  },

  // Refresh token
  async refreshToken() {
    try {
      const { apiBaseUrl } = getApiConfig();
      const response = await fetch(`${apiBaseUrl}/auth/refresh`, {
        method: 'POST'
      });
      return handleResponse(response);
    } catch (error) {
      console.error('Error refreshing token:', error);
      throw error;
    }
  }
}; 